import React, { forwardRef, useRef, useMemo } from 'react';
import {
  useComposedRef,
} from '@udecode/plate';
import { useDraggableState } from '@udecode/plate-dnd';
import clsx from 'clsx';

import './plate-draggable.scss';

const DRAGGABLE_ELEMENTS = ['img', 'table'];

export const PlateDraggable = forwardRef(
  (props, ref) => {
    const { children, element, onRenderDragHandle } = props;

    const DragHandle = onRenderDragHandle ?? DefaultDragHandle;

    const { dropLine, isDragging, rootRef, dragRef } = useDraggableState(props);

    const isDraggable = useMemo(() => DRAGGABLE_ELEMENTS.includes(element.type), [element.type]);

    return (
      <DraggableRoot
        className={clsx('plate-draggable', isDragging && 'is-dragging')}
        rootRef={rootRef}
        ref={ref}
      >
        {isDraggable && (
          <div
            contentEditable={false}
            className={clsx('plate-draggable__gutter-left')}
          >
            <div
              className={clsx('plate-draggable__toolbar-wrapper')}
            >
              <DraggableBlockToolbar
                dragRef={dragRef}
                element={element}
                className={clsx('plate-draggable__toolbar')}
              >
                <DragHandle
                  element={element}
                  className={clsx('plate-draggable__drag-handle')}
                />
              </DraggableBlockToolbar>
            </div>
          </div>
        )}

        <DraggableBlock
          className={clsx(
            'plate-draggable__block',
            `plate-draggable__block--${element.type}`,
          )}
        >
          {children}

          {!!dropLine && (
            <div
              contentEditable={false}
              className={clsx(
                'plate-draggable__drop-line',
                !!dropLine && dropLine,
              )}
            />
          )}
        </DraggableBlock>
      </DraggableRoot>
    );
  },
);

export const useDraggableRootProps = ({ rootRef, ...props }) => {
  return {
    ...props,
    ref: useComposedRef(props.ref, rootRef),
  };
};

export const DraggableRoot = (props) => {
  const htmlProps = useDraggableRootProps(props);

  return (
    <div {...htmlProps} />
  );
};

export const useDraggableBlockProps = (props) => {
  const blockRef = useRef(null);

  return {
    ...props,
    ref: useComposedRef(props.ref, blockRef),
  };
};

export const DraggableBlock = (props) => {
  const htmlProps = useDraggableBlockProps(props);

  return (
    <div {...htmlProps} />
  );
};

export const useDraggableBlockToolbarProps = ({
  element,
  dragRef,
  ...props
}) => {
  const dragWrapperRef = useRef(null);

  const multiDragRef = useComposedRef(dragRef, dragWrapperRef);

  return {
    ...props,
    ref: useComposedRef(props.ref, multiDragRef),
  };
};

export const DraggableBlockToolbar = (props) => {
  const htmlProps = useDraggableBlockToolbarProps(props);

  return (
    <div {...htmlProps} />
  );
};

export const DefaultDragHandle = (props) => {
  return (
    <button
      {...props}
      onMouseDown={(e) => e.preventDefault()}
    />
  );
};
